<svelte:options runes={false} />
<script>
  import { strategyByKey } from '../../lib/strategies.js';
  import { fromRequestSymbol } from '../../lib/symbolFormat.js';
  import SymbolUniverse from '../shared/SymbolUniverse.svelte';

  // The run's input parameters (result.parameters, camelCase). Null on results that predate parameter
  // persistence — the parent guards, but we also no-op defensively here.
  export let parameters = null;

  const FREQ_LABELS = {
    60: '1m', 300: '5m', 900: '15m', 1800: '30m',
    3600: '1h', 7200: '2h', 21600: '6h', 86400: '1d',
  };
  const SIZING_LABELS = { 0: 'Equal Split', 1: 'Inverse Volatility', 2: 'ATR-based' };

  $: strategyName = parameters ? (strategyByKey(parameters.strategy)?.name ?? parameters.strategy) : '';
  $: freqLabel = parameters ? (FREQ_LABELS[parameters.frequencySeconds] ?? `${parameters.frequencySeconds}s`) : '';
  $: sizingLabel = parameters ? (SIZING_LABELS[parameters.sizingLogic] ?? `#${parameters.sizingLogic}`) : '';
  // Symbols persist in request (dash/native) form; SymbolUniverse renders them in slash form, collapsed to
  // one row (a Shishito backtest carries the ~400-name server-built panel since migration 40).
  $: symbolsCsv = parameters?.symbols || '';
  $: benchmarkDisplay = parameters?.benchmarkSymbol ? fromRequestSymbol(parameters.benchmarkSymbol) : '—';

  function fmtDate(v) {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  }
  function fmtCash(v) {
    return v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Number(v).toLocaleString('en-US');
  }

  // Parse the serialized Tuning JSON (or object) into label/value rows for display. Empty → no rows.
  $: tuningRows = parseTuning(parameters?.tuning);
  function parseTuning(tuning) {
    if (!tuning) return [];
    let obj = tuning;
    if (typeof tuning === 'string') {
      try { obj = JSON.parse(tuning); } catch { return []; }
    }
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined)
      .map(([k, v]) => ({
        key: k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim(),
        value: Array.isArray(v)
          ? (Array.isArray(v[0]) ? v.map((p) => p.join(':')).join(', ') : v.join(', '))
          : String(v),
      }));
  }
</script>

{#if parameters}
  <details class="bt-run-params">
    <summary>Run parameters</summary>
    <dl class="bt-run-params-grid">
      <div><dt>Strategy</dt><dd>{strategyName}</dd></div>
      <div class="k2-dl-span">
        <dt>Symbols</dt>
        <dd><SymbolUniverse symbols={symbolsCsv} variant="block" label="Symbols" emptyText="—" /></dd>
      </div>
      <div><dt>Window</dt><dd>{fmtDate(parameters.windowStart)} – {fmtDate(parameters.windowEnd)}</dd></div>
      <div><dt>Frequency</dt><dd>{freqLabel}</dd></div>
      <div><dt>Starting cash</dt><dd>{fmtCash(parameters.startingCash)}</dd></div>
      <div><dt>Position sizing</dt><dd>{sizingLabel}</dd></div>
      <div><dt>Risk factor</dt><dd>{parameters.riskFactor}</dd></div>
      <div><dt>Protective stops</dt><dd>{parameters.enableStopOrders ? 'Enabled' : 'Disabled'}</dd></div>
      <div><dt>Benchmark</dt><dd>{benchmarkDisplay}</dd></div>
    </dl>
    {#if tuningRows.length}
      <h5 class="bt-run-params-subhead">Strategy tuning</h5>
      <dl class="bt-run-params-grid">
        {#each tuningRows as row}
          <div><dt>{row.key}</dt><dd>{row.value}</dd></div>
        {/each}
      </dl>
    {/if}
  </details>
{/if}

<style>
  .bt-run-params {
    margin: var(--spacing-sm, 0.5rem) 0;
    border: 1px solid var(--border-subtle, #1e1f22);
    border-radius: 6px;
    background: var(--surface-1, rgba(255, 255, 255, 0.02));
  }
  .bt-run-params summary {
    cursor: pointer;
    padding: 0.5rem 0.75rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text-secondary, #b5b6b8);
    user-select: none;
  }
  .bt-run-params[open] summary {
    border-bottom: 1px solid var(--border-subtle, #1e1f22);
  }
  .bt-run-params-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.5rem 1.25rem;
    margin: 0;
    padding: 0.75rem;
  }
  .bt-run-params-grid > div {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
  }
  .bt-run-params-grid dt {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--text-muted, #8a8b8d);
  }
  .bt-run-params-grid dd {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-primary, #e8e9ea);
    font-variant-numeric: tabular-nums;
  }
  .bt-run-params-subhead {
    margin: 0;
    padding: 0 0.75rem;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--text-muted, #8a8b8d);
  }
</style>
